import type { paths } from "@/api-schema";
import { SubjectRoles } from "@/api-schema-enums";
import { getFullUserName, UserNamePart } from "@/common/helpers/get-full-user-name";
import { hasDevStageDeveloperAccess } from "@/common/helpers/has-dev-stage-developer-access";
import { alphartisApiAxios, getTokenProvider } from "@/config/axios";
import postHog from "@/config/posthog";
import Sentry from "@/config/sentry";
import { jwtDecode } from "jwt-decode";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

const RETRY_ATTEMPTS = 2;
const ASSET_ACCESS_RENEWAL_LEEWAY_MS = 60_000;

type UserSearchResponse = paths["/v1/user/search"]["post"]["responses"][200]["content"]["application/json"];
type AssetAccessResponse = { expiresAt: string };

export interface DecodedApiToken {
  upn: string;
  sub: string;
  roles: string[];
  idp?: string;
}

export interface DecodedGraphToken {
  oid: string;
}

export type User = {
  email: string;
  sub: string;
  firstName: string | null;
  lastName: string | null;
  azureObjectUuid: string;
  roles: SubjectRoles[];
  uuid: string;
  employeeId: string | null;
};

export class AssetAccessError extends Error {
  constructor(cause: unknown) {
    super(`Unable to establish private asset access: ${cause instanceof Error ? cause.message : String(cause)}`);
    this.name = "AssetAccessError";
  }
}

export type UserStoreState = {
  user: User | null;
  isLoading: boolean;
  assetAccessExpiresAt: string | null;
  setUser: (user: User) => void;
  clearUser: () => void;
  loadUserData: () => Promise<void>;
  renewAssetAccess: () => Promise<void>;
  renewExpiringAssetAccess: () => Promise<void>;
};

async function fetchUserWithRetry(azureObjectUuid: string): Promise<UserSearchResponse> {
  for (let attempt = 0; attempt < RETRY_ATTEMPTS; attempt++) {
    const response = await alphartisApiAxios.post<UserSearchResponse>("/v1/user/search", {
      azureObjectUuid: [azureObjectUuid],
    });
    const total = response.data.meta?.total ?? response.data.data?.length ?? 0;
    if (total > 0) {
      return response.data;
    }
  }
  throw new Error("User not found in backend");
}

async function fetchAndBuildUser(): Promise<User> {
  const tokenProvider = getTokenProvider();
  if (!tokenProvider) {
    throw new Error("Token provider not initialized");
  }

  const { apiToken, msGraphToken } = await tokenProvider();

  const decodedApiToken: DecodedApiToken = jwtDecode(apiToken);
  const decodedGraphToken: DecodedGraphToken = jwtDecode(msGraphToken);

  const userDataResponse = await fetchUserWithRetry(decodedGraphToken.oid);
  const userData = userDataResponse.data[0];

  return {
    email: decodedApiToken.upn,
    sub: decodedApiToken.sub,
    firstName: userData.firstName,
    lastName: userData.lastName,
    azureObjectUuid: decodedGraphToken.oid,
    roles: assignUserRoles(decodedApiToken),
    uuid: userData.uuid,
    employeeId: userData.employeeId,
  };
}

async function fetchAssetAccessExpiry(): Promise<string> {
  try {
    const response = await alphartisApiAxios.get<AssetAccessResponse>("/v1/asset-access", { withCredentials: true });
    const expiresAt = new Date(response.data.expiresAt);

    if (Number.isNaN(expiresAt.getTime()) || expiresAt.getTime() <= Date.now()) {
      throw new Error("The asset access endpoint returned an invalid expiry.");
    }

    return response.data.expiresAt;
  } catch (error) {
    throw new AssetAccessError(error);
  }
}

export function getAssetAccessRenewalDelay(expiresAt: string): number {
  return Math.max(new Date(expiresAt).getTime() - Date.now() - ASSET_ACCESS_RENEWAL_LEEWAY_MS, 0);
}

export function assignUserRoles(decodedToken: DecodedApiToken): SubjectRoles[] {
  const roles = (decodedToken.roles as SubjectRoles[]) || [];

  if (hasDevStageDeveloperAccess(roles)) {
    return [...roles, SubjectRoles.Int];
  }

  if (decodedToken.idp) {
    return [...roles, SubjectRoles.Ext];
  }

  return [...roles, SubjectRoles.Int];
}

let assetAccessRenewal: Promise<void> | null = null;

function setupTracking(uuid: string, email: string, name?: string) {
  postHog.identify(uuid, { email, name });
  Sentry.setUser({ uuid, email: email, username: name });
}

function cancelTracking() {
  postHog.reset();
  Sentry.setUser(null);
}

export const userStore = create<UserStoreState>()(
  devtools(
    (set, get) => ({
      user: null,
      isLoading: false,
      assetAccessExpiresAt: null,

      setUser: (user: User) => {
        setupTracking(
          user.uuid,
          user.email,
          getFullUserName({ user: user, startingPart: UserNamePart.LastName, separator: ", " }),
        );
        set({ user, isLoading: false }, false, "user-store/set-user");
      },

      clearUser: () => {
        cancelTracking();
        set({ user: null, isLoading: false, assetAccessExpiresAt: null }, false, "user-store/clear-user");
      },

      loadUserData: async () => {
        set({ isLoading: true }, false, "user-store/load-user-data-start");
        try {
          const [user, assetAccessExpiresAt] = await Promise.all([fetchAndBuildUser(), fetchAssetAccessExpiry()]);
          setupTracking(
            user.uuid,
            user.email,
            getFullUserName({ user: user, startingPart: UserNamePart.LastName, separator: ", " }),
          );
          set({ user, isLoading: false, assetAccessExpiresAt }, false, "user-store/load-user-data-success");
        } catch (error) {
          set({ isLoading: false }, false, "user-store/load-user-data-error");
          throw error;
        }
      },

      renewAssetAccess: () => {
        // Many images can fail at once after the cookie expired; they should share one renewal request.
        assetAccessRenewal ??= fetchAssetAccessExpiry()
          .then((assetAccessExpiresAt) => {
            set({ assetAccessExpiresAt }, false, "user-store/renew-asset-access");
          })
          .finally(() => {
            assetAccessRenewal = null;
          });
        return assetAccessRenewal;
      },

      renewExpiringAssetAccess: async () => {
        const { assetAccessExpiresAt, renewAssetAccess } = get();
        if (assetAccessExpiresAt && getAssetAccessRenewalDelay(assetAccessExpiresAt) === 0) {
          await renewAssetAccess();
        }
      },
    }),
    { name: "user-store" },
  ),
);
